import { useMemo, useState, type CSSProperties } from "react";
import { ApiError } from "@/api/client";
import type { WorkMode } from "@/api/types";
import {
  useAttendanceMonth,
  useCompOffBalance,
  useCompOffLedger,
  useEnterPastAttendance,
  useHolidays,
} from "@/api/queries";
import { PageHeader } from "@/app/page-header";
import { StatTile, TrendChart } from "@/components/charts";
import { CheckInCard } from "./check-in-card";
import { AttendanceStatusBadge } from "@/pages/hr/attendance";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Field, Input, Select } from "@/components/ui/field";
import { Icon } from "@/components/ui/icon";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { EmptyState, SkeletonTable } from "@/components/ui/states";
import { TBody, TD, TDPrimary, TH, THead, TR, TableWrap } from "@/components/ui/table";
import { toast } from "@/components/ui/toast";
import {
  CELL_LABEL,
  classifyDay,
  currentMonth,
  holidayMap,
  leaveByDate,
  wfhByDate,
  monthBounds,
  eachDateInRange,
  shiftMonth,
  calendarTodayStr,
  todayStr,
  weekdayIndex,
  WEEKDAY_LABELS,
  type CellKind,
} from "@/lib/calendar";
import { formatDate, formatDays, formatTime } from "@/lib/format";
import { leaveLabel } from "@/lib/labels";
import { formatMinutes } from "@/lib/org-time";
import { cn } from "@/lib/cn";

/**
 * The day's status rides a small dot in the corner rather than flooding the
 * whole cell. Filling every tile with saturated colour made the grid shout and
 * left no room for the figure that actually matters, which is hours worked.
 *
 * `null` means no dot at all: a future date has nothing to report yet.
 */
const DOT: Record<CellKind, { fill: string; ring: string } | null> = {
  complete: { fill: "var(--dot-good)", ring: "var(--dot-ring-good)" },
  half: { fill: "var(--dot-warn)", ring: "var(--dot-ring-warn)" },
  inactive: { fill: "var(--dot-rest)", ring: "var(--dot-ring-rest)" },
  absent: { fill: "var(--dot-bad)", ring: "var(--dot-ring-bad)" },
  rest: { fill: "var(--dot-rest)", ring: "var(--dot-ring-rest)" },
  holiday: { fill: "var(--dot-rest)", ring: "var(--dot-ring-rest)" },
  future: null,
  // Approved leave is a solid dot; a request nobody has decided yet is the same
  // hue left hollow — the fill is the cell's own surface, so it reads as an
  // outline. That difference is shape, not colour, so the two survive both a
  // greyscale print and every form of colour blindness, which matters here
  // because "granted" and "asked for" are exactly the pair not to confuse.
  leave: { fill: "var(--dot-leave)", ring: "var(--dot-ring-leave)" },
  "leave-pending": { fill: "var(--surface)", ring: "var(--dot-ring-leave)" },
  // An upcoming day approved to be worked FROM HOME — a day worked, not a day
  // off, so it takes its own hue rather than a shade of the leave blue. Once the
  // day is actually worked it classifies as complete or half like any other, so
  // this dot only ever appears on days still to come.
  wfh: { fill: "var(--dot-wfh)", ring: "var(--dot-ring-wfh)" },
};

/**
 * A solid neon dot, plus a 1px ring in a darker step of its own hue.
 *
 * The ring is not decoration: neon is built for a dark surface, and on the pale
 * cell the green and yellow measure ~1.2:1, which is invisible. The ring gives
 * the fill an edge to read against without touching the colour. In dark mode
 * the ring token repeats the fill, so it disappears and the neon stands alone.
 */
function dotStyle(kind: CellKind): CSSProperties | undefined {
  const dot = DOT[kind];
  if (!dot) return undefined;
  return {
    backgroundColor: `rgb(${dot.fill})`,
    boxShadow: `inset 0 0 0 1px rgb(${dot.ring})`,
  };
}

export function MyAttendancePage() {
  const [month, setMonth] = useState(currentMonth());
  const monthData = useAttendanceMonth(month);
  const holidays = useHolidays();
  const compOffBalance = useCompOffBalance();
  const compOffLedger = useCompOffLedger();
  const enterPast = useEnterPastAttendance();

  const holidayNames = useMemo(() => holidayMap(holidays.data), [holidays.data]);
  const bounds = useMemo(() => monthBounds(month), [month]);
  const records = monthData.data?.records ?? [];
  const sessionsByDate = monthData.data?.sessionsByDate ?? {};
  // This month's leave, by date — so a day away reads as leave rather than as a
  // day this person failed to log.
  const leave = useMemo(() => leaveByDate(monthData.data?.leave), [monthData.data?.leave]);
  // This month's approved work-from-home days, so an upcoming one reads as
  // planned rather than as a blank square. A WFH day already worked is a full or
  // half day like any other and needs nothing here.
  const wfh = useMemo(() => wfhByDate(monthData.data?.wfh), [monthData.data?.wfh]);

  const byDate = useMemo(() => {
    const map = new Map<string, (typeof records)[number]>();
    for (const record of records) map.set(record.date, record);
    return map;
  }, [records]);

  const days = useMemo(() => eachDateInRange(bounds.start, bounds.end), [bounds]);
  const leadingBlanks = days[0] ? weekdayIndex(days[0]) : 0;

  const hasRemote = useMemo(() => records.some((r) => r.mode === "remote"), [records]);

  const tally = useMemo(() => {
    const counts = { complete: 0, half: 0, leave: 0, pending: 0, absent: 0, rest: 0, wfh: 0 };
    for (const date of days) {
      const kind = classifyDay(date, byDate.get(date), holidayNames, leave.get(date), wfh.get(date));
      // An upcoming work-from-home day counts towards nothing else: it is a plan,
      // not a day worked, absent or off. It has its own tally so the legend can
      // appear only when the month actually contains one.
      if (kind === "wfh") counts.wfh += 1;
      else if (kind === "complete") counts.complete += 1;
      else if (kind === "half") counts.half += 1;
      else if (kind === "leave") counts.leave += 1;
      // Pending is counted twice on purpose: once on its own, so the tile can
      // say how many days are still waiting on somebody, and once under "no
      // record", because until it is granted it is not yet an authorised
      // absence and pretending otherwise would let applying for leave stand in
      // for being given it.
      else if (kind === "leave-pending") {
        counts.pending += 1;
        counts.absent += 1;
      } else if (kind === "absent" || kind === "inactive") counts.absent += 1;
      else if (kind === "rest" || kind === "holiday") counts.rest += 1;
    }
    return counts;
  }, [days, byDate, holidayNames, leave, wfh]);

  // Only key states actually present this month get a legend entry.
  const hasLeave = tally.leave > 0;
  const hasPending = tally.pending > 0;
  const hasWfh = tally.wfh > 0;

  // Every mode is judged on hours now, so every worked day belongs on the trend.
  // The figure is the day's TOTAL across its sessions, not the gap between the
  // first arrival and the last departure — that would pay for the breaks.
  const hoursTrend = useMemo(
    () =>
      [...records]
        .sort((a, b) => a.date.localeCompare(b.date))
        .filter((record) => record.total_minutes > 0)
        .map((record) => ({
          day: formatDate(record.date, "d MMM") ?? record.date,
          hours: Math.round((record.total_minutes / 60) * 10) / 10,
        })),
    [records],
  );

  const isCurrentMonth = month === currentMonth();

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Your account"
        title="My attendance"
        sub="Check in, check out, and review your own history. A day that has been recorded cannot be edited afterwards."
      />

      <div className="grid gap-4 lg:grid-cols-[1fr_1.4fr]">
        <CheckInCard />
        <div className="grid gap-4 sm:grid-cols-2">
          <StatTile
            label="Full days this month"
            value={tally.complete}
            icon="check-circle"
            loading={monthData.isLoading}
            meta="4.5 hours or more across the day's sessions"
          />
          <StatTile
            label="Half days"
            value={tally.half}
            icon="warning-circle"
            loading={monthData.isLoading}
            meta="Under 4.5 hours, or a day never checked out of"
          />
          <StatTile
            label="On leave"
            value={tally.leave}
            icon="leave"
            loading={monthData.isLoading}
            meta={
              tally.pending > 0
                ? `Approved · ${tally.pending} more awaiting approval`
                : "Approved leave days this month"
            }
          />
          <StatTile
            label="No record"
            value={tally.absent}
            icon="x-circle"
            loading={monthData.isLoading}
            meta="Working days with nothing logged or approved"
          />
          <StatTile
            className="sm:col-span-2"
            label="Comp off available"
            value={formatDays(compOffBalance.data?.available)}
            icon="calendar-check"
            loading={compOffBalance.isLoading}
            meta={`${formatDays(compOffBalance.data?.earned)} earned`}
          />
        </div>
      </div>

      <Panel>
        <PanelHeader
          title={formatDate(`${month}-01`, "MMMM yyyy") ?? month}
          note="Weekly offs, company holidays and approved leave are shown but never counted as absence"
          actions={
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Previous month"
                onClick={() => setMonth(shiftMonth(month, -1))}
              >
                <Icon name="caret-left" size={16} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Next month"
                disabled={isCurrentMonth}
                onClick={() => setMonth(shiftMonth(month, 1))}
              >
                <Icon name="caret-right" size={16} />
              </Button>
            </div>
          }
        />
        <div className="p-4">
          {monthData.isLoading ? (
            <div className="grid grid-cols-7 gap-1.5">
              {Array.from({ length: 35 }).map((_, i) => (
                <div key={i} className="aspect-[2/1] animate-pulse rounded-lg bg-surface-sunken" />
              ))}
            </div>
          ) : (
            <>
              <div className="mb-1.5 grid grid-cols-7 gap-1.5">
                {WEEKDAY_LABELS.map((label) => (
                  <span key={label} className="text-center text-xs text-muted">
                    {label}
                  </span>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1.5">
                {Array.from({ length: leadingBlanks }).map((_, i) => (
                  <span key={`blank-${i}`} />
                ))}
                {days.map((date) => {
                  const onLeave = leave.get(date);
                  const fromHome = wfh.get(date);
                  const kind = classifyDay(date, byDate.get(date), holidayNames, onLeave, fromHome);
                  const record = byDate.get(date);
                  // The day's total across its sessions — every mode is judged on
                  // it now, so every mode shows it.
                  const minutes = record?.total_minutes ?? 0;
                  const hours = minutes > 0 ? Math.round((minutes / 60) * 10) / 10 : null;
                  const isToday = date === todayStr();
                  return (
                    <span
                      key={date}
                      /* A day already WORKED from home keeps its own status and
                         says so here — otherwise the fact is lost the moment the
                         day stops being upcoming. */
                      title={`${formatDate(date)} · ${holidayNames.get(date) ?? CELL_LABEL[kind]}${
                        onLeave ? ` (${leaveLabel(onLeave.leave_type)})` : ""
                      }${hours ? ` · ${hours}h worked` : ""}${
                        fromHome?.status === "approved" && kind !== "wfh" ? " · from home" : ""
                      }`}
                      className={cn(
                        "relative flex aspect-[2/1] flex-col justify-between rounded-lg border px-1.5 py-1",
                        kind === "future"
                          ? "border-line/60 bg-surface"
                          : "border-line bg-surface-sunken/50",
                        isToday && "ring-2 ring-accent ring-offset-2 ring-offset-surface",
                      )}
                    >
                      <span
                        className={cn(
                          "font-mono text-xs tabular-nums leading-none",
                          kind === "future" ? "text-faint" : isToday ? "font-semibold text-accent" : "text-ink-soft",
                        )}
                      >
                        {Number(date.slice(-2))}
                      </span>

                      {DOT[kind] && (
                        <span
                          aria-hidden
                          className="absolute right-1.5 top-1 h-2 w-2 rounded-full"
                          style={dotStyle(kind)}
                        />
                      )}

                      {/* Hours worked is the figure this grid exists to show. A
                          declared day off has none, so it says so instead. */}
                      {/* Hours is what the cell normally shows. A day with no
                          hours to show says what it was instead, rather than
                          sitting blank and leaving the dot to carry it alone. */}
                      <span className="text-center font-mono text-[11px] leading-none tabular-nums text-ink">
                        {hours !== null ? (
                          `${hours}h`
                        ) : kind === "inactive" ? (
                          CELL_LABEL[kind]
                        ) : kind === "leave" ? (
                          /* The cell is two units wide and "Menstrual Leave"
                             is not going to fit in it. The word here is the
                             state; the type is in the cell's title. */
                          <span className="text-[10px]">Leave</span>
                        ) : kind === "leave-pending" ? (
                          <span className="text-[10px] text-muted">Pending</span>
                        ) : (
                          <span className="text-faint">&nbsp;</span>
                        )}
                      </span>
                    </span>
                  );
                })}
              </div>
              <ul className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5">
                {(
                  [
                    "complete",
                    "half",
                    ...(hasLeave ? (["leave"] as const) : []),
                    ...(hasPending ? (["leave-pending"] as const) : []),
                    ...(hasWfh ? (["wfh"] as const) : []),
                    "absent",
                    ...(hasRemote ? (["inactive"] as const) : []),
                    "rest",
                  ] as CellKind[]
                ).map((kind) => (
                  <li key={kind} className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full" style={dotStyle(kind)} aria-hidden />
                    <span className="text-xs text-ink-soft">{CELL_LABEL[kind]}</span>
                  </li>
                ))}
                <li className="ml-auto text-xs text-muted">Figure in each cell is hours worked</li>
              </ul>
            </>
          )}
        </div>
      </Panel>

      <TrendChart
        title="Hours worked"
        note={`Completed days in ${formatDate(`${month}-01`, "MMMM yyyy")}`}
        data={hoursTrend}
        xKey="day"
        series={[{ key: "hours", label: "Hours" }]}
        area
        valueSuffix="h"
        loading={monthData.isLoading}
        emptyBody="No completed days this month yet."
      />

      <BackfillPanel
        onSubmit={(input) =>
          enterPast.mutate(input, {
            onSuccess: () => toast.success("Attendance recorded."),
            onError: (err) =>
              toast.error(err instanceof ApiError ? err.message : "Could not record that day."),
          })
        }
        busy={enterPast.isPending}
        workMode={monthData.data?.workMode}
      />

      <Panel>
        <PanelHeader title="This month's log" />
        {monthData.isLoading ? (
          <SkeletonTable rows={6} cols={5} />
        ) : records.length === 0 ? (
          <EmptyState
            icon="attendance"
            title="Nothing recorded this month"
            body="Check in from the card above, or add a past day you missed."
          />
        ) : (
          <TableWrap>
            <THead>
              <TH>Date</TH>
              <TH>Mode</TH>
              <TH>First in</TH>
              <TH>Last out</TH>
              <TH numeric>Sessions</TH>
              <TH numeric>Total</TH>
              <TH>Status</TH>
              <TH>Edited</TH>
            </THead>
            <TBody>
              {[...records]
                .sort((a, b) => b.date.localeCompare(a.date))
                .map((record) => {
                  // Same four inputs the calendar grid above uses. Without the
                  // leave and WFH maps this table called a sanctioned absence
                  // "No record" on the very day the grid painted "On leave".
                  const kind = classifyDay(
                    record.date,
                    record,
                    holidayNames,
                    leave.get(record.date),
                    wfh.get(record.date),
                  );
                  return (
                    <TR key={record.date}>
                      <TDPrimary className="whitespace-nowrap font-mono text-xs">
                        {formatDate(record.date)}
                      </TDPrimary>
                      <TD className="capitalize">{record.mode ?? "—"}</TD>
                      <TD className="font-mono text-xs">{formatTime(record.check_in) ?? "—"}</TD>
                      <TD className="font-mono text-xs">{formatTime(record.check_out) ?? "—"}</TD>
                      <TD numeric>{(sessionsByDate[record.date] ?? []).length || "—"}</TD>
                      <TD numeric className="font-mono text-xs">
                        {record.total_minutes > 0 ? formatMinutes(record.total_minutes) : "—"}
                      </TD>
                      <TD>
                        <AttendanceStatusBadge kind={kind} />
                      </TD>
                      <TD>
                        {record.edited_by_name ? (
                          <Badge tone="outline" icon="edit">
                            {record.edited_by_name}
                          </Badge>
                        ) : (
                          <span className="text-xs text-faint">—</span>
                        )}
                      </TD>
                    </TR>
                  );
                })}
            </TBody>
          </TableWrap>
        )}
      </Panel>

      <Panel>
        <PanelHeader
          title="Comp off ledger"
          note="Earned automatically for working a weekly off or company holiday"
        />
        {compOffLedger.isLoading ? (
          <SkeletonTable rows={4} cols={4} />
        ) : (compOffLedger.data ?? []).length === 0 ? (
          <EmptyState
            icon="calendar-check"
            title="No comp off yet"
            body="Nine hours or more on a rest day credits a full day, 4.5 to 9 hours credits half, and less than 4.5 hours credits nothing."
          />
        ) : (
          <TableWrap>
            <THead>
              <TH>Date</TH>
              <TH>Entry</TH>
              <TH numeric>Days</TH>
              <TH>Reason</TH>
            </THead>
            <TBody>
              {(compOffLedger.data ?? []).map((entry, index) => (
                <TR key={`${entry.date}-${index}`}>
                  <TDPrimary className="whitespace-nowrap font-mono text-xs">
                    {formatDate(entry.date)}
                  </TDPrimary>
                  <TD>
                    <Badge tone={entry.type === "Earned" ? "good" : "neutral"}>{entry.type}</Badge>
                  </TD>
                  <TD numeric>{entry.amount}</TD>
                  <TD>{entry.reason}</TD>
                </TR>
              ))}
            </TBody>
          </TableWrap>
        )}
      </Panel>
    </div>
  );
}

/** Adds a past day that has no record. Deliberately not an edit form. */
function BackfillPanel({
  onSubmit,
  busy,
  workMode,
}: {
  onSubmit: (input: {
    date: string;
    mode: "onsite" | "remote";
    checkIn?: string;
    checkOut?: string;
    status?: "full" | "half" | "off";
  }) => void;
  busy: boolean;
  workMode: WorkMode | undefined;
}) {
  const [date, setDate] = useState("");
  /*
    Only a hybrid account picks. For everyone else the mode IS the account's
    work mode, so it is derived on every render rather than seeded into state:
    this panel mounts before the month query answers, and a value seeded then
    keeps whatever it was born with — an onsite-looking, disabled field on a
    remote account, submitting a mode the server goes on to reject.
  */
  const [pickedMode, setPickedMode] = useState<"onsite" | "remote">("onsite");
  const mode: "onsite" | "remote" =
    workMode === "remote" ? "remote" : workMode === "onsite" ? "onsite" : pickedMode;
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  // A remote day is declared rather than timed, so backfilling one asks for the
  // same thing the button would have recorded on the day itself.
  const [declared, setDeclared] = useState<"full" | "half" | "off">("full");
  const [error, setError] = useState<string | null>(null);

  function submit() {
    setError(null);
    if (!workMode) {
      setError("Still loading your work mode — try again in a moment.");
      return;
    }
    if (!date) {
      setError("Pick the day you want to add.");
      return;
    }
    // The server checks this against the org's calendar date, so this must too:
    // between midnight and 6am todayStr() is yesterday, and this form used to
    // refuse a day the backend would happily have accepted.
    if (date >= calendarTodayStr()) {
      setError("You can only add a day that has already passed.");
      return;
    }
    if (mode === "remote") {
      onSubmit({ date, mode, status: declared });
      return;
    }
    if (!checkIn || !checkOut) {
      setError("Both times are required.");
      return;
    }
    onSubmit({ date, mode, checkIn, checkOut });
  }

  return (
    <Panel>
      <PanelHeader
        title="Add a day you missed"
        note="Only for a past day with no record. Once a day is recorded it cannot be changed."
      />
      <div className="p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Date" htmlFor="bf-date" required>
            <Input
              id="bf-date"
              type="date"
              max={calendarTodayStr()}
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </Field>
          <Field label="Mode" htmlFor="bf-mode" required>
            <Select
              id="bf-mode"
              value={mode}
              disabled={workMode !== "hybrid"}
              onChange={(e) => setPickedMode(e.target.value as "onsite" | "remote")}
            >
              <option value="onsite">Onsite</option>
              <option value="remote">Remote</option>
            </Select>
          </Field>
          {mode === "remote" ? (
            <Field label="The day was" htmlFor="bf-declared" required>
              <Select
                id="bf-declared"
                value={declared}
                onChange={(e) => setDeclared(e.target.value as "full" | "half" | "off")}
              >
                <option value="full">A full day</option>
                <option value="half">A half day</option>
                <option value="off">A day off</option>
              </Select>
            </Field>
          ) : (
            <>
              <Field label="Start time" htmlFor="bf-in" required>
                <Input
                  id="bf-in"
                  type="time"
                  value={checkIn}
                  onChange={(e) => setCheckIn(e.target.value)}
                />
              </Field>
              <Field label="End time" htmlFor="bf-out" required>
                <Input
                  id="bf-out"
                  type="time"
                  value={checkOut}
                  onChange={(e) => setCheckOut(e.target.value)}
                />
              </Field>
            </>
          )}
        </div>

        {mode === "remote" && (
          <p className="mt-3 text-xs leading-relaxed text-muted">
            Remote days are recorded as a full or half day, not as hours.
          </p>
        )}

        {error && (
          <p role="alert" className="mt-3 flex items-start gap-1.5 text-xs text-bad">
            <Icon name="warning-circle" size={16} />
            <span>{error}</span>
          </p>
        )}

        <Button
          variant="primary"
          className="mt-4"
          iconLeft="plus"
          onClick={submit}
          disabled={!workMode}
          loading={busy}
        >
          Add record
        </Button>
      </div>
    </Panel>
  );
}
